import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { getTenantSelection } from "@/auth/tenantSelection";
type SecurityHandlers = {
  reason: () => Promise<string>;
  reauth: () => Promise<void>;
};
let security: SecurityHandlers | null = null;
let reasonPending: Promise<string> | null = null;
let authPending: Promise<void> | null = null;
export function setSecurityHandlers(handlers: SecurityHandlers | null) {
  security = handlers;
}
const instance = axios.create({
  baseURL:
    import.meta.env.VITE_API_URL || "https://job-api.ooak.jp/api/v1/admin/",
  timeout: 60000,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
});
instance.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.set("Authorization", "Bearer " + token);
  const tenant = getTenantSelection();
  if (
    tenant &&
    !/^(auth|platform|notifications|invitations)\//.test(config.url ?? "") &&
    config.url !== "notifications"
  ) {
    config.headers.set("X-Tenant-Type", tenant.type);
    config.headers.set("X-Tenant-Id", String(tenant.id));
  }
  const reason = sessionStorage.getItem("job-admin-reason");
  if (reason) config.headers.set("X-Admin-Reason", reason);
  return config;
});
instance.interceptors.response.use(
  (response) => response,
  async (failure) => {
    if (failure.response?.data instanceof Blob && failure.response.data.type.includes("json")) {
      try { failure.response.data = JSON.parse(await failure.response.data.text()); } catch { /* Preserve the original response if it is not JSON. */ }
    }
    const config = failure.config;
    if (config && !config._securityRetried && security) {
      if (failure.response?.status === 428) {
        authPending ??= security.reauth().finally(() => {
          authPending = null;
        });
        await authPending;
        config._securityRetried = true;
        return instance.request(config);
      }
      if (
        failure.response?.status === 422 &&
        failure.response?.data?.message?.includes("administration reason")
      ) {
        reasonPending ??= security.reason().finally(() => {
          reasonPending = null;
        });
        const reason = await reasonPending;
        config.headers.set("X-Admin-Reason", reason);
        config._securityRetried = true;
        return instance.request(config);
      }
    }
    if (
      failure.response?.status === 401 &&
      !/^(auth\/(login|forgot-password|reset-password)|invitations)/.test(
        config?.url ?? "",
      )
    ) {
      localStorage.removeItem("token");
      sessionStorage.removeItem("job-admin-tenant");
      window.location.assign("/login");
    }
    return Promise.reject(failure);
  },
);
const responseBody = (response: AxiosResponse) => response;
export default {
  get: (url: string, config?: AxiosRequestConfig) =>
    instance.get(url, config).then(responseBody),
  post: (url: string, body: object, headers?: { [key: string]: string }) =>
    instance.post(url, body, { headers }).then(responseBody),
  put: (url: string, body: object) =>
    instance.put(url, body).then(responseBody),
  patch: (url: string, body: object) =>
    instance.patch(url, body).then(responseBody),
  download: (url: string, body: object) =>
    instance.post(url, body, { responseType: "blob" }).then(responseBody),
  delete: (url: string, body?: object) =>
    instance.delete(url, { data: body }).then(responseBody),
};
